import { useTranslation } from 'react-i18next';
import { addSpaceAfterComma } from '~/features/catalog/utils/articleUtil';
import { PRICECODE_WEIGHTCONTROLLED } from '~/common/constants/ppConstants';
import { formatDateByLanguage } from '~/common/utils/dateUtil';
import type { IArticleTranslatedTextDto } from '~/dtos/hub/hub.models.generated';

interface KeyFeatureListProps<
  T extends {
    articleNumber: string;
    foodFact: string;
    lateinName: string;
    ingredients: string;
    specialDiet: IArticleTranslatedTextDto[];
    allergenContains: IArticleTranslatedTextDto[];
    allergenMayContains: IArticleTranslatedTextDto[];
    abgefuellt: string[];
    abgepackt: string[];
    showAlcoholPercentage: boolean;
    hergestellt: IArticleTranslatedTextDto[];
    drainedWeight: string;
    factRegion: string;
    aufgetaut: string;
    storability: string;
    durability: string;
    preparation: string;
    fishingmethod: string;
    fishingZones: string;
    inAquakulturGewonnen: boolean;
    rohstoffHerkunft: IArticleTranslatedTextDto[];
    orderNote: string;
    fitsWellWith: string;
    grapes: string;
    alcoholVolPercent?: number;
    servingTemperature: string;
    fakturaText: string;
    quantityPallet: number;
    sellAmount: number;
    sellUnit: string;
    priceCode: number;
    unitText: string;
    descriptionLong: string;
  }
> {
  article: T;
  specialDeliveryDate?: Date;
  orderEndTimesText?: string;
}

function KeyValueItem({ title, value }: Readonly<{ title: string; value?: string }>) {
  if (!value) return null;

  return (
    <div className={'flex flex-col desktop:flex-row desktop:gap-4 border-t desktop:min-h-[50px] desktop:items-center'}>
      <div className={'font-bold desktop:font-normal desktop:min-w-[300px] pt-2 desktop:pt-0'}>
        <h3 className={'text-base desktop:font-normal'}>{title}</h3>
      </div>
      <div className={'pb-2 desktop:pb-0'} data-testid={`key-value-${title}`}>
        {value}
      </div>
    </div>
  );
}

export function KeyFeatureList<
  T extends {
    articleNumber: string;
    foodFact: string;
    lateinName: string;
    ingredients: string;
    specialDiet: IArticleTranslatedTextDto[];
    allergenContains: IArticleTranslatedTextDto[];
    allergenMayContains: IArticleTranslatedTextDto[];
    abgefuellt: string[];
    abgepackt: string[];
    showAlcoholPercentage: boolean;
    hergestellt: IArticleTranslatedTextDto[];
    drainedWeight: string;
    factRegion: string;
    aufgetaut: string;
    storability: string;
    durability: string;
    preparation: string;
    fishingmethod: string;
    fishingZones: string;
    inAquakulturGewonnen: boolean;
    rohstoffHerkunft: IArticleTranslatedTextDto[];
    orderNote: string;
    fitsWellWith: string;
    grapes: string;
    alcoholVolPercent?: number;
    servingTemperature: string;
    fakturaText: string;
    quantityPallet: number;
    sellAmount: number;
    sellUnit: string;
    priceCode: number;
    unitText: string;
    descriptionLong: string;
  }
>({ article, specialDeliveryDate, orderEndTimesText }: Readonly<KeyFeatureListProps<T>>) {
  const { t, i18n } = useTranslation();

  const hasAnyAllergene =
    (article.allergenContains && article.allergenContains.length > 0) ||
    (article.allergenMayContains && article.allergenMayContains.length > 0);

  return (
    <>
      <KeyValueItem title={t('Art. Nr.')} value={article.articleNumber} />
      {article.descriptionLong && <KeyValueItem title={t('Beschreibung')} value={article.descriptionLong} />}
      <KeyValueItem title={t('Sachbezeichnung')} value={article.foodFact} />
      <KeyValueItem title={t('Lateinischer Name')} value={article.lateinName} />
      <KeyValueItem title={t('Zutaten')} value={addSpaceAfterComma(article.ingredients)} />
      <KeyValueItem title={t('Spezialernährung')} value={article.specialDiet?.map(x => x.text).join(', ')} />
      {hasAnyAllergene && (
        <div
          className={'flex flex-col desktop:flex-row desktop:gap-4 border-t desktop:min-h-[50px] desktop:items-center'}
        >
          <h3 className={'text-base desktop:font-normal font-bold desktop:min-w-[300px] pt-2 desktop:pt-0'}>
            {t('Allergene')}
          </h3>
          <div>
            {article.allergenContains && article.allergenContains.length > 0 && (
              <div>
                <span>{`${t('Enthält Allergene')}:  `}</span>
                <span className={'font-bold'} data-testid={`key-feature-allergens-may`}>
                  {article.allergenContains?.map(x => x.text).join(', ')}
                </span>
              </div>
            )}

            {article.allergenMayContains && article.allergenMayContains.length > 0 && (
              <div>
                <span>{`${t('Kann enthalten')}:  `}</span>
                <span className={'font-bold'} data-testid={`key-feature-allergens`}>
                  {article.allergenMayContains?.map(x => x.text).join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {orderEndTimesText && <KeyValueItem title={t('Bestellschlusszeit')} value={orderEndTimesText} />}
      <AmountPerPalletRow article={article} />
      {specialDeliveryDate && (
        <KeyValueItem
          title={t('Frühester Liefertermin')}
          value={formatDateByLanguage(specialDeliveryDate, i18n.language)}
        />
      )}
      <KeyValueItem title={t('Region')} value={addSpaceAfterComma(article.factRegion)} />
      <KeyValueItem title={t('Rohstoffherkunft')} value={article.rohstoffHerkunft?.map(x => x.text).join(', ')} />
      <KeyValueItem title={t('Produktionsland')} value={article.hergestellt?.map(x => x.text).join(', ')} />
      <KeyValueItem title={t('Artikel abgefüllt in')} value={article.abgefuellt?.join(', ')} />
      <KeyValueItem title={t('Artikel abgepackt in')} value={article.abgepackt?.join(', ')} />
      <FishingZoneRow fishingZones={article.fishingZones} inAquakulturGewonnen={article.inAquakulturGewonnen} />
      <KeyValueItem title={t('Fischfangmethode')} value={addSpaceAfterComma(article.fishingmethod)} />
      <KeyValueItem title={t('Abtropfgewicht')} value={article.drainedWeight} />
      <KeyValueItem title={t('Lagerfähigkeit')} value={article.storability} />
      <KeyValueItem title={t('Serviertemperatur')} value={article.servingTemperature} />
      <KeyValueItem title={t('Traubensorten')} value={addSpaceAfterComma(article.grapes)} />
      <KeyValueItem title={t('Passt zu')} value={addSpaceAfterComma(article.fitsWellWith)} />
      <KeyValueItem title={t('Bestellhinweis')} value={article.orderNote} />
      <KeyValueItem title={t('Deklaration nach LDV')} value={article.fakturaText} />
      <KeyValueItem title={t('Auftauhinweis')} value={article.aufgetaut} />
      <KeyValueItem title={t('Aufbewahrungshinweis')} value={article.durability} />
      <KeyValueItem title={t('Zubereitung')} value={article.preparation} />
      {article.showAlcoholPercentage && (
        <KeyValueItem title={t('Alkoholgehalt')} value={article.alcoholVolPercent?.toFixed(1) + '%'} />
      )}
    </>
  );
}

function FishingZoneRow({
  fishingZones,
  inAquakulturGewonnen
}: Readonly<{ fishingZones: string; inAquakulturGewonnen: boolean }>) {
  const { t } = useTranslation();

  const fishingZone = addSpaceAfterComma(fishingZones);
  let displayValue = '';
  if (fishingZone && fishingZone.length > 0) {
    displayValue += `${t('Gefangen in')}: ${fishingZone}`;

    if (inAquakulturGewonnen) {
      const aquaKultur = `${t('In Aquakultur gewonnen')}`;
      if (aquaKultur) {
        displayValue += ` (${aquaKultur.charAt(0).toLowerCase() + aquaKultur.slice(1)})`;
      }
    }
  } else if (inAquakulturGewonnen) {
    displayValue += `${t('In Aquakultur gewonnen')}`;
  }

  return <KeyValueItem title={t('Fischfangzone')} value={displayValue} />;
}

function AmountPerPalletRow<
  T extends {
    quantityPallet: number;
    sellAmount: number;
    sellUnit: string;
    priceCode: number;
    unitText: string;
  }
>({ article }: Readonly<{ article: T }>) {
  const { t } = useTranslation();

  if (
    !article.quantityPallet ||
    !article.sellAmount ||
    article.quantityPallet === article.sellAmount ||
    article.priceCode === PRICECODE_WEIGHTCONTROLLED
  ) {
    return null;
  }

  const amountPerPallet = `${(article.quantityPallet ?? 0) / article.sellAmount}${article.sellUnit ? ' ' + article.sellUnit : ''} x ${article.sellAmount} ${article.unitText}`;

  return <KeyValueItem title={t('Menge pro Palette')} value={amountPerPallet} />;
}
